import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, setSessionExpiredHandler } from "../api";
import { profileConfirmed } from "./roles";
import { useLiveRefresh } from "../liveRefresh";

const AuthContext = createContext(null);

/**
 * The claims of the token the browser is holding, or null.
 *
 * Never throws — both callers below run during render, so a malformed or
 * absent token has to degrade quietly rather than take a tab down.
 */
function tokenClaims() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const [, payload] = token.split(".");
    if (!payload) return null;
    // JWTs are base64**url**: '-' and '_' stand in for '+' and '/', and the
    // '=' padding is stripped. atob() rejects that, so a plain atob(payload)
    // works only for the tokens that happen to contain neither character —
    // i.e. it fails intermittently, which is the worst way for this to fail.
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")));
  } catch {
    return null;
  }
}

/**
 * When the current sign-in session ends — no endpoint and no round trip.
 *
 * This is what lets the Submissions/Results tabs say "ends at 20:33" rather
 * than only "8 hours", which is the difference between a fact and something
 * the reader has to combine with when they happened to sign in.
 */
export function sessionExpiresAt() {
  const exp = tokenClaims()?.exp;
  return exp ? new Date(exp * 1000) : null;
}

/**
 * How long *this* session was issued for, in hours — not how long the setting
 * currently says new ones last.
 *
 * The two differ for every session that was already open when an admin changed
 * `token_expire_hours`, and a JWT's expiry cannot be revised after issue: it is
 * inside the signed payload. Quoting the setting there produced a banner
 * reading "48 hours" above a countdown to 8, which looks like the portal
 * reverting something rather than like a token predating the change.
 *
 * Returns null for a token minted before `iat` was added, so the caller can
 * fall back to the configured value.
 */
export function sessionLengthHours() {
  const c = tokenClaims();
  if (!c?.exp || !c?.iat) return null;
  const hours = (c.exp - c.iat) / 3600;
  return hours > 0 ? Math.round(hours) : null;
}

/**
 * The sign-out mark LoginPage reads (SIGNOUT, 2026-09-29).
 *
 * Keycloak returns from its end-session endpoint to a plain /login — the
 * `post_logout_redirect_uri` is matched against the client's registered URI,
 * so it cannot carry `?signed_out=1` — and the sign-in page has to know anyway,
 * or a replayed sign-in puts a user back and the page forwards to `next`. This
 * tab's sessionStorage survives that round trip; a sign-in removes it. Storage
 * can throw (a private window, blocked site data), and a sign-out must not.
 */
const SIGNED_OUT_KEY = "portal_signed_out";
const markSignedOut = on => {
  try {
    if (on) sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    else sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch { /* the URL's ?signed_out=1 still covers the page it lands on */ }
};

/** What a person owes when nothing is known, or nobody is signed in. */
const NO_DUTIES = { blocking: 0, total: 0, signatures: 0, waiting: 0 };

/**
 * `/api/me/documents` reduced to the four counts the banner and the badge show.
 *
 * The `waiting` fallback is for a server that predates the field, and it has
 * to count the same three things the server does — it was missing
 * `pending_approval`, so an admin with only a version to approve saw a badge
 * of 0 beside a card telling them something was waiting.
 */
const dutiesOf = r => ({
  blocking: r.blocking || 0,
  total: r.total || 0,
  signatures: (r.signatures || []).length,
  waiting: r.waiting ?? ((r.total || 0) + (r.signatures || []).length
                         + (r.pending_approval || []).length),
});

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Signing out is a state change, not a page load. `api.js` used to do
    // `window.location.href = "/login"`, and a full document navigation aborts
    // every in-flight XHR — which is how a running upload was torn down by an
    // unrelated background poll (JOB-529B91). Clearing the user is enough:
    // ProtectedRoute already routes to /login when there is none, inside the
    // router, without touching anything the page is still doing.
    setSessionExpiredHandler(() => setUser(null));

    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(u => setUser(u))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  // The password sign-in, for a local account. A Keycloak account comes back
  // through /login/complete and `loginWithToken` instead.
  const login = async (email, password) => {
    const form = new URLSearchParams();
    // OAuth2's password form names the field `username`; it carries the e-mail.
    form.append("username", email);
    form.append("password", password);
    const res = await fetch("/auth/token", { method: "POST", body: form });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || "Login failed");
    return loginWithToken(body.access_token);
  };

  /**
   * Take a portal token that arrived some other way than a password POST.
   *
   * The Keycloak callback hands the browser a portal JWT in the fragment of
   * `/login/complete`, and this is what that page does with it — the same two
   * steps `login` does after its POST, in one place so the two cannot drift.
   * A token `/auth/me` refuses is removed again before the error goes up: a
   * refused token left in storage would be attached to every later request.
   */
  const loginWithToken = async token => {
    localStorage.setItem("token", token);
    try {
      const me = await api.get("/auth/me");
      markSignedOut(false);
      setUser(me);
      return me;
    } catch (err) {
      localStorage.removeItem("token");
      throw err;
    }
  };

  /**
   * End the session: tell the server, drop the token, and — for a Keycloak
   * session — end it at Keycloak too.
   *
   * `POST /auth/logout` is what writes the `auth.logout` audit row, so it goes
   * out first, while the token is still attached (`request` reads it before
   * its first await). It is best-effort: the browser's session is cleared at
   * once, whether the server answers, refuses or never answers.
   *
   * A Keycloak session (`amr` from `/auth/me`) then leaves the SPA for
   * `/api/auth/oidc/logout`, which sends the browser to Keycloak's end-session
   * endpoint: without it the next *Sign in with …* went straight back into the
   * same Keycloak account. That full navigation would abort the POST, so it
   * waits for the POST to settle. A password session, or one of unknown kind,
   * never goes near Keycloak; the caller routes it to /login.
   */
  const logout = async () => {
    const toKeycloak = user?.amr === "keycloak";
    const told = api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("token");
    markSignedOut(true);
    setUser(null);
    if (toKeycloak) {
      await told;
      // `replace`: an /api/auth/oidc/* URL left in the history is one Back
      // away from a silent sign-in.
      window.location.replace("/api/auth/oidc/logout");
    }
  };

  /**
   * Re-read the signed-in account from the server.
   *
   * `user` is fetched once on mount and once on login, which was enough while
   * nothing the signed-in person did could change their own standing. Granting
   * yourself a role is the first operation where the actor and the target are
   * the same account, and without this the new badge, the nav and
   * `grantableAnywhere(me)` all keep the old answer until a reload.
   *
   * Never throws. It is a background consistency refresh, and a failed one must
   * not take down the page somebody is in the middle of editing.
   */
  const refreshUser = () => api.get("/auth/me").then(setUser).catch(() => {});

  /**
   * What this person still owes — documents to confirm, quotations to sign,
   * versions to approve — as the banner and the Projects badge show it.
   *
   * Held here, once, because it is answered in one place and changed in
   * several. It used to be a state private to `Layout`, re-read on navigation
   * only, so confirming a document on a project's card or on Personal
   * Administration cleared the card and left the banner asking for the same
   * document until a reload (DUTIES, 2026-10-06). Every action that confirms or
   * approves calls `refreshDuties()`; `Layout` calls it on navigation.
   *
   * Not asked for an account the facility has not confirmed: the server refuses
   * it everything but its own profile, so the read could only be a 403. Never
   * throws, like `refreshUser` — a failed background count must not take down
   * the page somebody just confirmed something on.
   */
  const [duties, setDuties] = useState(NO_DUTIES);
  const refreshDuties = useCallback(() => {
    if (!user || !profileConfirmed(user)) return Promise.resolve();
    return api.get("/api/me/documents").then(r => setDuties(dutiesOf(r || {}))).catch(() => {});
  }, [user]);
  // A signed-out browser owes nothing: the next account must not inherit the
  // last one's banner before its own answer arrives.
  useEffect(() => { if (!user) setDuties(NO_DUTIES); }, [user]);
  // Any change this tab makes may be one the banner counts — a quotation
  // signed, a version approved — and coming back to the tab may find one made
  // elsewhere. The explicit calls stay; this is the net under them.
  useLiveRefresh(refreshDuties);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout, refreshUser,
                                   duties, refreshDuties }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
